<template>
  <b-modal header-close-content="" v-model="_showModal" :title="computedGlossaryAddUpdateTitle">
    <div data-cy="add-update-glossary-modal">
      <b-form-group>
        <label class="font-weight-bold">{{ $t("Glossary.Name") }}</label>
        <input
          type="text"
          ref="input"
          class="form-control"
          :placeholder="$t('Glossary.EnterName')"
          v-model="vm.inputGlossaryName"
          v-tooltip="nameValidationFeedback"
          data-cy="glossary-name-input"
          :class="{ 'border-danger': nameValidationFeedback }" />
      </b-form-group>
    </div>
    <div>
      <BFormGroup>
        <b-row>
          <b-col class="col-6">
            <label class="font-weight-bold"> {{ $t("Glossary.SourceLanguage") }}</label>
            <b-dropdown
              class="w-100 dropdown-default"
              v-tooltip="languagePairValidationFeedback"
              data-cy="source-language-dropdown"
              :class="{ 'border-danger': languagePairValidationFeedback }">
              <template #button-content>
                <span class="button-content">{{
                  vm.selectedSourceLanguage == null ? `${$t("Glossary.ChooseSourceLanguage")}` : vm.selectedSourceLanguage.displayName
                }}</span>
              </template>
              <b-dropdown-item
                v-for="(lang, key) in vm.availableSourceLanguages"
                :key="key"
                :data-cy="'source-language-' + lang.languageCode"
                @click="handleSourceLanguageSelect(lang)">
                {{ lang.displayName }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col class="col-6">
            <label class="font-weight-bold"> {{ $t("Glossary.TargetLanguage") }}</label>
            <b-dropdown
              class="w-100 dropdown-default"
              v-tooltip="languagePairValidationFeedback"
              data-cy="target-language-dropdown"
              :class="{ 'border-danger': languagePairValidationFeedback }">
              <template #button-content>
                <span class="button-content">{{
                  vm.selectedTargetLanguage == null ? `${$t("Glossary.ChooseTargetLanguage")}` : vm.selectedTargetLanguage.displayName
                }}</span>
              </template>
              <b-dropdown-item
                v-for="(lang, key) in vm.availableTargetLanguages"
                :key="key"
                :data-cy="'target-language-' + lang.languageCode"
                @click="handleTargetLanguageSelect(lang)">
                {{ lang.displayName }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
        </b-row>
      </BFormGroup>
    </div>
    <template #modal-footer="{ cancel }">
      <b-btn
        @click="AddOrUpdate"
        :disabled="!validationFeedback || nameValidationFeedback !== undefined"
        variant="primary"
        data-cy="add-update-glossary-save-btn"
        >{{ $t("Save") }}</b-btn
      >
      <b-btn @click="cancel">{{ $t("Cancel") }}</b-btn>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref } from "vue";
import { GlossaryTypes } from "@medapro-types/AttributeManager/GlossaryTypes";
import { useFocus } from "@vueuse/core";
import { useI18n } from "vue-i18n";

const input = ref<HTMLInputElement | null>(null);

const props = defineProps<{
  showModal?: boolean;
  glossaryItems: GlossaryTypes.GlossaryItem[];
  selectedGlossary: GlossaryTypes.GlossaryItem | null;
  selectedSourceLanguage: GlossaryTypes.glossaryLanguage | null;
  selectedTargetLanguage: GlossaryTypes.glossaryLanguage | null;
  inputGlossaryName: string;
  availableSourceLanguages: GlossaryTypes.glossaryLanguage[];
  availableTargetLanguages: GlossaryTypes.glossaryLanguage[];
  glossaryLanguagePairs: GlossaryTypes.getGlossaryLanguagePairs[];
  isUpdateGlossary: boolean;
  isAddGlossary: boolean;
}>();

const emit = defineEmits(["update:showModal", "addOrUpdateFromModal"]);
const { t: $t } = useI18n();

//#region Viewmodel

type ViewModel = {
  isUpdateGlossary: boolean;
  isAddGlossary: boolean;
  selectedGlossary: GlossaryTypes.GlossaryItem | null;
  selectedSourceLanguage: GlossaryTypes.glossaryLanguage | null;
  selectedTargetLanguage: GlossaryTypes.glossaryLanguage | null;
  availableSourceLanguages: GlossaryTypes.glossaryLanguage[];
  availableTargetLanguages: GlossaryTypes.glossaryLanguage[];
  inputGlossaryName: string;
  glossaryItems: GlossaryTypes.GlossaryItem[];
  glossaryLanguagePairs: GlossaryTypes.getGlossaryLanguagePairs[];
};

const vm = reactive<ViewModel>({
  isUpdateGlossary: false,
  isAddGlossary: false,
  selectedGlossary: null,
  availableSourceLanguages: [],
  availableTargetLanguages: [],
  selectedSourceLanguage: null,
  selectedTargetLanguage: null,
  inputGlossaryName: "",
  glossaryItems: [],
  glossaryLanguagePairs: []
});

//#endregion

//#region Validation

const validationFeedback = computed(() => {
  const glossaryItemInput = {
    id: "",
    name: vm.inputGlossaryName,
    sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
    targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
    isActive: false
  };
  const addUpdateInputsEmpty = vm.inputGlossaryName.length !== 0 && vm.selectedSourceLanguage !== null && vm.selectedTargetLanguage !== null;
  const languagePairValid = vm.selectedSourceLanguage !== vm.selectedTargetLanguage;
  return addUpdateInputsEmpty && languagePairValid && !isDublicatedGlossar(glossaryItemInput) && checkGlossaryLanguagePairIsValid() ? true : false;
});

const nameValidationFeedback = computed(() => {
  const glossaryItemInput: GlossaryTypes.GlossaryItem = {
    id: "",
    name: vm.inputGlossaryName,
    sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
    targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
    isActive: false
  };
  if (vm.isAddGlossary) {
    glossaryItemInput.id = "";
  } else {
    if (!vm.selectedGlossary) return;
    glossaryItemInput.id = vm.selectedGlossary.id;
  }

  const isNameEmpty = vm.inputGlossaryName == null || vm.inputGlossaryName == "";
  if (isNameEmpty) return $t("Glossary.NameIsEmpty");
  else if (isDublicatedGlossar(glossaryItemInput)) return $t("Glossary.GlossaryExists");
  else if (vm.inputGlossaryName.trim().length > 255) return $t("Glossary.AnEntryHasToMuchChars");
  else return undefined;
});

const languagePairValidationFeedback = computed(() => {
  const glossaryItemInput: GlossaryTypes.GlossaryItem = {
    id: "",
    name: vm.inputGlossaryName,
    sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
    targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
    isActive: false
  };
  if (vm.isAddGlossary) {
    glossaryItemInput.id = "";
  } else {
    if (!vm.selectedGlossary) return;
    glossaryItemInput.id = vm.selectedGlossary.id;
  }
  if (isDublicatedGlossar(glossaryItemInput)) return $t("Glossary.ChooseLanguagePairWhichNotExist");
  else if (vm.selectedSourceLanguage == null && vm.selectedTargetLanguage == null) return undefined;
  else if (vm.selectedSourceLanguage?.languageCode == vm.selectedTargetLanguage?.languageCode) return $t("Glossary.ChooseLanguagePairWithDifferentValues");
  else return undefined;
});

//#endregion

//#region Manipulation / Helperfunctions

const computedGlossaryAddUpdateTitle = computed(() => {
  if (vm.isUpdateGlossary) {
    return $t("Glossary.GlossaryEditTitle");
  }
  return $t("Glossary.GlossaryAddTitle");
});

const _showModal = computed({
  get: () => {
    return props.showModal;
  },
  set: (nv) => {
    emit("update:showModal", nv);
  }
});

function handleSourceLanguageSelect(value: GlossaryTypes.glossaryLanguage) {
  vm.selectedSourceLanguage = value;
}
function handleTargetLanguageSelect(value: GlossaryTypes.glossaryLanguage) {
  vm.selectedTargetLanguage = value;
}

function isDublicatedGlossar(targetObject: GlossaryTypes.GlossaryItem) {
  if (vm.isUpdateGlossary) {
    for (let i = 0; i < vm.glossaryItems.length; i++) {
      const obj = vm.glossaryItems[i];
      if (
        obj.id !== targetObject.id &&
        obj.name === targetObject.name &&
        obj.sourceLanguage === targetObject.sourceLanguage &&
        obj.targetLanguage === targetObject.targetLanguage
      ) {
        return true;
      }
    }
    return false;
  } else {
    for (let i = 0; i < vm.glossaryItems.length; i++) {
      const obj = vm.glossaryItems[i];
      if (obj.name === targetObject.name && obj.sourceLanguage === targetObject.sourceLanguage && obj.targetLanguage === targetObject.targetLanguage) {
        return true;
      }
    }
    return false;
  }
}

function checkGlossaryLanguagePairIsValid() {
  const glossaryLanguagePairToCheck = {
    sourceLanguageCode: vm.selectedSourceLanguage?.languageCode,
    targetLanguageCode: vm.selectedTargetLanguage?.languageCode
  };
  const isGlossaryLanguagePairValid = vm.glossaryLanguagePairs.find(
    (x) =>
      x.sourceLanguage.languageCode == glossaryLanguagePairToCheck.sourceLanguageCode &&
      x.targetLanguage.languageCode == glossaryLanguagePairToCheck.targetLanguageCode
  );
  return isGlossaryLanguagePairValid !== undefined;
}

function AddOrUpdate() {
  if (vm.isUpdateGlossary) {
    if (!vm.selectedGlossary) return;
    const createdOrUpdatedGlossary: GlossaryTypes.GlossaryItemToUpdateExtended = {
      updateModel: {
        id: vm.selectedGlossary.id,
        name: vm.selectedGlossary.name,
        sourceLanguage: vm.selectedGlossary.sourceLanguage,
        targetLanguage: vm.selectedGlossary.targetLanguage,
        isActive: false,
        newName: vm.inputGlossaryName,
        newSourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
        newTargetLanguage: vm.selectedTargetLanguage?.languageCode as string
      }
    };
    emit("addOrUpdateFromModal", createdOrUpdatedGlossary, false, false);
  } else {
    const createdOrUpdatedGlossary: GlossaryTypes.GlossaryItemToUpdate = {
      updateModel: {
        id: "",
        name: vm.inputGlossaryName,
        sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
        targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
        isActive: false
      },
      entries: {}
    };
    emit("addOrUpdateFromModal", createdOrUpdatedGlossary, false, false);
  }
}

//#endregion

//#region Watcher

watch(
  () => props,
  () => {
    if (input.value) {
      useFocus(input, { initialValue: true });
    }
    Object.assign(vm, props);
  },
  { deep: true }
);

//#endregion
</script>
